import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { formatAccountTypeLabel } from "@/auth/roles";
import { getUsers } from "@/api/users";
import { PageHeader } from "@/components/PageHeader";
import { formatDate } from "@/lib/format";

export function UsersListPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["users"],
    queryFn: getUsers,
  });

  if (isLoading) return <p className="empty-state">Loading…</p>;
  if (isError) {
    return (
      <div className="callout danger">
        <strong>Could not load pilots</strong>
        <p>{error instanceof Error ? error.message : "Unknown error"}</p>
      </div>
    );
  }
  if (!data) return null;

  return (
    <>
      <PageHeader
        title="Pilots"
        lede="Browse pilots, spot instructor accounts immediately, and open any visible account profile or flight history."
        actions={
          <div className="stats-row">
            <article className="stat-card">
              <span>Pilots</span>
              <strong>{data.summary.pilots}</strong>
            </article>
            <article className="stat-card">
              <span>Instructors</span>
              <strong>{data.summary.instructors}</strong>
            </article>
            <article className="stat-card">
              <span>Active pilots</span>
              <strong>{data.summary.active_pilots}</strong>
            </article>
            <article className="stat-card">
              <span>Visible flights</span>
              <strong>{data.summary.flights}</strong>
            </article>
          </div>
        }
      />

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Pilot list</h2>
            <p>
              Pilots stay first in the directory. Instructor accounts are included below and
              marked clearly.
            </p>
          </div>
          <p>{data.users.length} records</p>
        </div>
        {data.users.length ? (
          <div className="table-shell">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Registered</th>
                  <th>Flight count</th>
                  <th>Last flight</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((user) => {
                  const roleLabel = formatAccountTypeLabel(user.account_type);
                  const roleClass =
                    user.account_type === "instructor"
                      ? "directory-role-badge-instructor"
                      : user.account_type === "admin"
                        ? "directory-role-badge-admin"
                        : "directory-role-badge-pilot";
                  return (
                    <tr
                      key={user.id}
                      className="clickable-row"
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest("a, button")) return;
                        navigate(`/users/${user.id}`);
                      }}
                    >
                      <td>
                        <div className="directory-user-cell">
                          <strong>{user.username}</strong>
                          <span
                            className={`directory-role-badge ${roleClass}`}
                          >
                            {roleLabel}
                          </span>
                        </div>
                        <small>ID {user.id}</small>
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>{user.flight_count}</td>
                      <td>{formatDate(user.last_flight_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-state">
            No pilot or instructor accounts have been registered yet.
          </p>
        )}
      </section>
    </>
  );
}
